import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Eye, X, Check, User, Users, UsersRound } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Scratches tab — TEMP-REGIONALS-2026 standalone implementation.
 *
 * Deliberately separate from the Entries flow:
 *   * Same filters (Competition / Event / Skater) as the Entries tab.
 *   * Same table layout, but the action column shows only View + an X
 *     button for "Scratch this skater's entries".
 *   * Clicking X opens a modal showing every entry the skater has in
 *     the same competition. Discipline + Grade are read-only; an X
 *     button per row toggles the scratch state. When toggled ON the
 *     event label is rendered with a strike-through. When toggled OFF
 *     the row reverts to plain text and a green "Unscratch" tooltip
 *     appears on the check icon.
 *
 * Backend writes a row to ``competition_scratches`` per scratch (POST
 * /api/scratches with {competition_id, entry_id}). The same endpoint
 * toggles off when the entry is currently scratched. Documents read
 * active scratches at render time.
 */
export function ScratchesTab({ entries, competitions }) {
  const [competitionFilter, setCompetitionFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const [skaterFilter, setSkaterFilter] = useState('');

  // Map of entry_id -> true for currently-scratched entries. Loaded
  // once + refreshed on every successful toggle.
  const [scratched, setScratched] = useState({});

  // Scratch dialog state: which skater we're scratching, and the list
  // of their entries in the selected competition.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEntries, setDialogEntries] = useState([]);
  const [dialogSkaterName, setDialogSkaterName] = useState('');
  const [dialogCompName, setDialogCompName] = useState('');
  const [togglingEntryId, setTogglingEntryId] = useState(null);

  // View dialog state — read-only summary of one entry.
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingEntry, setViewingEntry] = useState(null);

  // ---- Data loading ---------------------------------------------------

  // Load active scratches for the current filter scope.
  // Defined inside the effect (with the cleanup pattern) so the lint
  // rule against state-setting from a separately-named function in an
  // effect stays happy. ``setScratched`` only fires after the request
  // resolves and the component is still mounted.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const params =
          competitionFilter !== 'all'
            ? { competition_id: competitionFilter }
            : {};
        const res = await axios.get(`${API}/scratches`, {
          params,
          withCredentials: true,
        });
        if (cancelled) return;
        const next = {};
        (res.data || []).forEach((s) => {
          next[s.entry_id] = true;
        });
        setScratched(next);
      } catch (err) {
        console.error('Failed to load scratches:', err);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [competitionFilter]);

  // ---- Helpers --------------------------------------------------------

  const getSkaterDisplayName = (entry) => {
    if (entry.entry_type === 'individual' && entry.skater1) {
      return `${entry.skater1.first_name} ${entry.skater1.last_name}`;
    }
    if (entry.entry_type === 'pair') {
      const s1 = entry.skater1
        ? `${entry.skater1.first_name} ${entry.skater1.last_name}`
        : '?';
      const s2 = entry.skater2
        ? `${entry.skater2.first_name} ${entry.skater2.last_name}`
        : '?';
      return entry.team_name || `${s1} & ${s2}`;
    }
    if (entry.entry_type === 'team') {
      return (
        entry.team_name || `Team (${entry.team_skaters?.length || 0} skaters)`
      );
    }
    return '-';
  };

  const getEntryTypeIcon = (type) => {
    switch (type) {
      case 'individual':
        return <User className="h-4 w-4" />;
      case 'pair':
        return <Users className="h-4 w-4" />;
      case 'team':
        return <UsersRound className="h-4 w-4" />;
      default:
        return null;
    }
  };

  // Event-label helper, mirrors EntriesTab so the table reads the same.
  const eventLabel = (entry) =>
    entry.grade_name && entry.discipline_name
      ? `${entry.grade_name} ${entry.discipline_name}`
      : entry.grade_name || '-';

  // Event filter options (unique grades within the chosen competition).
  const getEventOptions = () => {
    const seen = new Map();
    entries.forEach((entry) => {
      if (
        competitionFilter !== 'all' &&
        entry.competition_id !== competitionFilter
      )
        return;
      if (!entry.grade_id) return;
      const label = eventLabel(entry);
      if (!label) return;
      const existing = seen.get(entry.grade_id);
      if (existing) existing.count += 1;
      else seen.set(entry.grade_id, { label, count: 1 });
    });
    return Array.from(seen.entries())
      .map(([grade_id, { label, count }]) => ({ grade_id, label, count }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  // Filter the entries list using the three filters.
  const filteredEntries = entries.filter((entry) => {
    const matchesCompetition =
      competitionFilter === 'all' || entry.competition_id === competitionFilter;
    const matchesEvent =
      eventFilter === 'all' || entry.grade_id === eventFilter;
    const matchesSkater =
      !skaterFilter ||
      getSkaterDisplayName(entry)
        .toLowerCase()
        .includes(skaterFilter.toLowerCase());
    return matchesCompetition && matchesEvent && matchesSkater;
  });

  // Group the filtered entries by (skater, competition) so the table
  // shows one row per "skater in this comp" rather than one per entry.
  // Clicking X opens a dialog with all of that skater's entries.
  const groupedRows = (() => {
    const groups = new Map();
    filteredEntries.forEach((entry) => {
      // Use skater1 for individuals; fall back to team_name for teams.
      const skaterKey =
        entry.entry_type === 'individual'
          ? entry.skater1?.skater_id || entry.entry_id
          : entry.entry_id; // teams/pairs: one row per entry
      const key = `${entry.competition_id}::${skaterKey}`;
      const existing = groups.get(key);
      if (existing) {
        existing.entries.push(entry);
      } else {
        groups.set(key, {
          key,
          competition_id: entry.competition_id,
          competition_short_name: entry.competition_short_name,
          skater_name: getSkaterDisplayName(entry),
          entry_type: entry.entry_type,
          entries: [entry],
        });
      }
    });
    return Array.from(groups.values()).sort((a, b) =>
      a.skater_name.localeCompare(b.skater_name)
    );
  })();

  // ---- Actions --------------------------------------------------------

  const openView = (entry) => {
    setViewingEntry(entry);
    setViewDialogOpen(true);
  };

  const openScratchDialog = (group) => {
    setDialogEntries(group.entries);
    setDialogSkaterName(group.skater_name);
    const comp = competitions.find(
      (c) => c.competition_id === group.competition_id
    );
    setDialogCompName(comp?.name || group.competition_short_name || '');
    setDialogOpen(true);
  };

  const toggleScratch = async (entry) => {
    setTogglingEntryId(entry.entry_id);
    try {
      const res = await axios.post(
        `${API}/scratches`,
        {
          competition_id: entry.competition_id,
          entry_id: entry.entry_id,
        },
        { withCredentials: true }
      );
      setScratched((prev) => ({
        ...prev,
        [entry.entry_id]: !!res.data?.scratched,
      }));
      toast.success(
        res.data?.scratched ? 'Entry scratched' : 'Scratch removed'
      );
    } catch (err) {
      toast.error(
        err.response?.data?.detail || 'Failed to update scratch state'
      );
    } finally {
      setTogglingEntryId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
        <Select
          value={competitionFilter}
          onValueChange={(value) => {
            setCompetitionFilter(value);
            setEventFilter('all');
          }}
        >
          <SelectTrigger
            className="w-[250px]"
            data-testid="scratch-competition-filter"
          >
            <SelectValue placeholder="Filter by competition" />
          </SelectTrigger>
          <SelectContent className="z-[200]">
            <SelectItem value="all">All Competitions</SelectItem>
            {competitions.map((comp) => (
              <SelectItem key={comp.competition_id} value={comp.competition_id}>
                {comp.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={eventFilter} onValueChange={setEventFilter}>
          <SelectTrigger
            className="w-[260px]"
            data-testid="scratch-event-filter"
          >
            <SelectValue placeholder="Filter by event" />
          </SelectTrigger>
          <SelectContent className="z-[200] max-h-[400px]">
            <SelectItem value="all">All Events</SelectItem>
            {getEventOptions().map((opt) => (
              <SelectItem key={opt.grade_id} value={opt.grade_id}>
                {opt.label} ({opt.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          placeholder="Search by skater name…"
          value={skaterFilter}
          onChange={(e) => setSkaterFilter(e.target.value)}
          className="w-[260px]"
          data-testid="scratch-skater-filter"
        />
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="scratches-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-stone-600">
                  Skater(s)
                </th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">
                  Events
                </th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">
                  Competition
                </th>
                <th className="text-center py-3 px-4 font-medium text-stone-600">
                  Scratched
                </th>
                <th className="text-center py-3 px-4 font-medium text-stone-600 w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {groupedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center py-8 text-muted-foreground"
                  >
                    {entries.length === 0
                      ? 'No entries yet.'
                      : 'No entries match your filters.'}
                  </td>
                </tr>
              ) : (
                groupedRows.map((g) => {
                  const scratchCount = g.entries.filter(
                    (e) => scratched[e.entry_id]
                  ).length;
                  return (
                    <tr
                      key={g.key}
                      className="border-b border-stone-100 hover:bg-stone-50"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 font-medium">
                          {getEntryTypeIcon(g.entry_type)}
                          {g.skater_name}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-stone-700">
                        <div className="flex flex-col gap-0.5">
                          {g.entries.map((e) => (
                            <span
                              key={e.entry_id}
                              className={
                                scratched[e.entry_id]
                                  ? 'line-through text-stone-400'
                                  : ''
                              }
                            >
                              {eventLabel(e)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {g.competition_short_name || '-'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {scratchCount > 0 ? (
                          <Badge className="bg-red-100 text-red-800">
                            {scratchCount} of {g.entries.length}
                          </Badge>
                        ) : (
                          <span className="text-stone-300">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openView(g.entries[0])}
                            className="action-icon"
                            title="View"
                            data-testid={`view-scratch-${g.key}`}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openScratchDialog(g)}
                            className="action-icon text-red-600 hover:text-red-700"
                            title="Scratch"
                            data-testid={`scratch-${g.key}`}
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Scratch / Unscratch dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="scratch-dialog">
          <DialogHeader>
            <DialogTitle>Scratch entries — {dialogSkaterName}</DialogTitle>
            <DialogDescription>
              Click <X className="inline h-4 w-4" /> next to an event to
              scratch the skater from it.{' '}
              {dialogCompName ? `Competition: ${dialogCompName}.` : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="border rounded-md divide-y">
            {dialogEntries.map((entry) => {
              const isScratched = !!scratched[entry.entry_id];
              return (
                <div
                  key={entry.entry_id}
                  className="flex items-center justify-between p-3"
                >
                  <div className="flex flex-col">
                    <span className="text-xs uppercase tracking-wide text-stone-400">
                      Event
                    </span>
                    <span
                      className={
                        isScratched
                          ? 'line-through text-stone-400 font-medium'
                          : 'font-medium'
                      }
                    >
                      {eventLabel(entry)}
                    </span>
                  </div>
                  <button
                    onClick={() => toggleScratch(entry)}
                    disabled={togglingEntryId === entry.entry_id}
                    className={`action-icon ${
                      isScratched
                        ? 'text-green-600 hover:text-green-700'
                        : 'text-red-600 hover:text-red-700'
                    }`}
                    title={isScratched ? 'Unscratch' : 'Scratch'}
                    data-testid={`toggle-scratch-${entry.entry_id}`}
                  >
                    {isScratched ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <X className="h-5 w-5" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="scratch-dialog-close"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View dialog — read-only summary */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent data-testid="scratch-view-dialog">
          <DialogHeader>
            <DialogTitle>Entry details</DialogTitle>
          </DialogHeader>
          {viewingEntry && (
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-stone-500">Skater(s): </span>
                <span className="font-medium">
                  {getSkaterDisplayName(viewingEntry)}
                </span>
              </div>
              <div>
                <span className="text-stone-500">Event: </span>
                <span
                  className={
                    scratched[viewingEntry.entry_id]
                      ? 'line-through text-stone-400 font-medium'
                      : 'font-medium'
                  }
                >
                  {eventLabel(viewingEntry)}
                </span>
              </div>
              <div>
                <span className="text-stone-500">Competition: </span>
                <span className="font-medium">
                  {viewingEntry.competition_short_name || '-'}
                </span>
              </div>
              <div>
                <span className="text-stone-500">Entry # </span>
                <span className="font-medium">{viewingEntry.entry_number}</span>
              </div>
              <div>
                <span className="text-stone-500">Entry type: </span>
                <span className="font-medium capitalize">
                  {viewingEntry.entry_type}
                </span>
              </div>
              <div>
                <span className="text-stone-500">Scratched: </span>
                <span className="font-medium">
                  {scratched[viewingEntry.entry_id] ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
